import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Warning, PaperclipHorizontal, X, File, CheckCircle, Circle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const CATEGORIES = [
  "Utilities","Rent","Professional Fees","Supplies","Repairs and Maintenance",
  "Taxes and Government Fees","Contractor / Service Provider","Marketing","Travel","Others"
];

const PAYMENT_TYPES = ["Check","Bank Transfer","Wire Transfer","Cash","Credit Card"];

const FALLBACK_REQUEST_TYPES = [
  { code: 'reimbursement', name: 'Reimbursement' },
  { code: 'purchase_request', name: 'Purchase Request' },
  { code: 'cheque_preparation', name: 'Cheque Preparation' },
  { code: 'cash_advance', name: 'Cash Advance' },
  { code: 'liquidation', name: 'Liquidation' },
  { code: 'vendor_payment', name: 'Vendor Payment' },
  { code: 'others', name: 'Others' }
];

export default function CreatePaymentRequest() {
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);
  const editData = location.state?.editData || null;
  const isEdit = !!editData;

  const [vendors, setVendors] = useState([]);
  // badyet-cost-centre: fetched from Badyet via RFP, never hardcoded here.
  const [costCentres, setCostCentres] = useState([]);
  const [ccUnavailable, setCcUnavailable] = useState(null);
  useEffect(() => {
    api.get('/payments/cost-centres')
      .then(r => {
        setCostCentres(r.data.cost_centres || []);
        setCcUnavailable(r.data.available ? null : (r.data.reason || 'unavailable'));
      })
      // An empty dropdown and a broken one look the same to the person filling
      // the form. Say which it is.
      .catch(() => setCcUnavailable('the budget service could not be reached'));
  }, []);
  const [contracts, setContracts] = useState([]);
  const [requestTypes, setRequestTypes] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState('');
  const [files, setFiles] = useState([]);
  const [checklistFiles, setChecklistFiles] = useState({});

  const [form, setForm] = useState({
    request_type: editData?.request_type || '',
    vendor_id: editData?.vendor_id || '',
    contract_id: editData?.contract_id || '',
    category: editData?.category || '',
    cost_centre: editData?.cost_centre || '',   // badyet-cost-centre
    amount: editData?.amount ? String(editData.amount) : '',
    description: editData?.description || '',
    due_date: editData?.due_date || '',
    payment_type: editData?.payment_type || 'Bank Transfer',
    notes: editData?.notes || ''
  });

  useEffect(() => {
    api.get('/vendors', { params: { status: 'active', limit: 500 } })
      .then(r => setVendors(r.data.items || []))
      .catch(() => toast.error('Failed to load vendors'));

    api.get('/admin/request-types')
      .then(r => setRequestTypes((r.data || []).filter(x => x.active !== false)))
      .catch(() => setRequestTypes(FALLBACK_REQUEST_TYPES));

    api.get('/admin/checklist-templates')
      .then(r => setChecklists(r.data || []))
      .catch(() => setChecklists([]));
  }, []);

  useEffect(() => {
    if (form.vendor_id) {
      api.get('/contracts', { params: { vendor_id: form.vendor_id, limit: 100 } })
        .then(r => setContracts(r.data.items || []))
        .catch(() => setContracts([]));
    } else {
      setContracts([]);
    }
  }, [form.vendor_id]);

  const selectedRequestType = useMemo(
    () => requestTypes.find(rt => rt.code === form.request_type),
    [requestTypes, form.request_type]
  );

  const selectedChecklist = useMemo(
    () => checklists.find(cl => cl.request_type_code === form.request_type),
    [checklists, form.request_type]
  );

  const handleChange = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const handleChecklistFile = (documentTypeCode, file) => {
    if (!file) return;
    setChecklistFiles(prev => ({ ...prev, [documentTypeCode]: file }));
  };

  const removeChecklistFile = (documentTypeCode) => {
    setChecklistFiles(prev => {
      const copy = { ...prev };
      delete copy[documentTypeCode];
      return copy;
    });
  };

  const handleFileAdd = (e) => {
    const newFiles = Array.from(e.target.files || []);
    setFiles(prev => [...prev, ...newFiles]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadFiles = async (requestId) => {
    for (const [documentTypeCode, file] of Object.entries(checklistFiles)) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        await api.post(`/payments/${requestId}/attachments`, formData, {
          params: {
            checklist_item_id: documentTypeCode,
            document_type: documentTypeCode
          },
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        await api.post(`/payments/${requestId}/attachments`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
  };

  const validateForm = () => {
    if (!form.request_type) {
      toast.error('Select request type first');
      return false;
    }

    if (!form.vendor_id || !form.category || !form.cost_centre || !form.amount || !form.description || !form.due_date) {
      toast.error('Please fill in all required fields');
      return false;
    }

    const requiredItems = selectedChecklist?.items?.filter(i => i.required !== false) || [];
    const missing = requiredItems.filter(i => !checklistFiles[i.document_type_code]);

    if (missing.length > 0) {
      toast.error(`Missing required document(s): ${missing.map(i => i.label).join(', ')}`);
      return false;
    }

    return true;
  };

  const handleSubmit = async (asDraft = true) => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount)
      };

      if (!payload.contract_id) delete payload.contract_id;

      let requestId;

      if (isEdit && editData.id) {
        await api.put(`/payments/${editData.id}`, payload);
        requestId = editData.id;
      } else {
        const { data } = await api.post('/payments', payload);
        if (data.duplicate_warning) setDuplicateWarning(data.duplicate_warning);
        requestId = data.request?.id || data.id;
      }

      if ((files.length > 0 || Object.keys(checklistFiles).length > 0) && requestId) {
        await uploadFiles(requestId);
      }

      if (!asDraft && requestId) {
        await api.post(`/payments/${requestId}/submit`);
        toast.success(isEdit ? 'Request updated and resubmitted' : 'Request created and submitted');
      } else {
        toast.success(isEdit ? 'Request updated and saved as draft' : 'Request saved as draft');
      }

      navigate(`/payments/${requestId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save request');
    } finally {
      setLoading(false);
    }
  };

  const isFormLocked = !form.request_type;

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="create-payment-page">
      <h1 className="font-heading text-3xl font-bold tracking-tight">
        {isEdit ? 'Edit Request' : 'New Request'}
      </h1>

      {isEdit && (
        <div className="flex items-center gap-2 p-3 rounded-sm bg-[#0055FF]/5 border border-[#0055FF]/20 text-[#0055FF] text-sm">
          Editing {editData.request_code} — make your changes and resubmit.
        </div>
      )}

      {duplicateWarning && (
        <div className="flex items-center gap-2 p-3 rounded-sm bg-[#FEF9C3] border border-[#EAB308]/30 text-[#854D0E] text-sm" data-testid="duplicate-warning">
          <Warning size={18} /> {duplicateWarning}
        </div>
      )}

      <Card className="rounded-sm border border-[#0055FF]/20">
        <CardHeader>
          <CardTitle className="text-base font-semibold">Step 1: Select Request Type</CardTitle>
        </CardHeader>
        <CardContent>
          <Label className="text-xs uppercase tracking-[0.05em] font-medium text-[#475569]">
            Request Type
          </Label>
          <Select
            value={form.request_type}
            onValueChange={v => handleChange('request_type', v)}
            disabled={isEdit}
          >
            <SelectTrigger className="rounded-sm mt-1" data-testid="request-type-select">
              <SelectValue placeholder="Select request type first" />
            </SelectTrigger>
            <SelectContent className="rounded-sm">
              {(requestTypes.length ? requestTypes : FALLBACK_REQUEST_TYPES).map(rt => (
                <SelectItem key={rt.code} value={rt.code}>{rt.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {selectedRequestType && (
            <div className="mt-3 text-sm text-[#475569]">
              Selected: <span className="font-semibold text-[#0F172A]">{selectedRequestType.name}</span>
            </div>
          )}

          {!selectedRequestType && (
            <div className="mt-3 text-sm text-[#854D0E] bg-[#FEF9C3] border border-[#EAB308]/30 rounded-sm p-3">
              Choose the request type first. The system will use this to determine the required documents and approval workflow.
            </div>
          )}
        </CardContent>
      </Card>

      {selectedChecklist && (
        <Card className="rounded-sm border">
          <CardHeader>
            <CardTitle className="text-base font-semibold">Step 2: Required Document Checklist</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(selectedChecklist.items || []).map((item, idx) => {
                const attachedFile = checklistFiles[item.document_type_code];
                return (
                  <div key={`${item.document_type_code}-${idx}`} className="flex items-center justify-between gap-3 p-3 rounded-sm bg-[#F8FAFC] border">
                    <div className="flex items-center gap-3">
                      {attachedFile ? (
                        <CheckCircle size={20} className="text-[#16A34A]" />
                      ) : (
                        <Circle size={20} className="text-[#94A3B8]" />
                      )}
                      <div>
                        <div className="text-sm font-medium">
                          {item.label}
                          {item.required !== false && <span className="text-[#EF4444] ml-1">*</span>}
                        </div>
                        <div className="text-xs text-[#64748B]">{item.document_type_code}</div>
                        {attachedFile && (
                          <div className="text-xs text-[#166534] mt-1">
                            Attached: {attachedFile.name}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <input
                        id={`checklist-file-${item.document_type_code}`}
                        type="file"
                        className="hidden"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                        onChange={(e) => handleChecklistFile(item.document_type_code, e.target.files?.[0])}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => document.getElementById(`checklist-file-${item.document_type_code}`)?.click()}
                      >
                        {attachedFile ? 'Replace' : 'Upload'}
                      </Button>
                      {attachedFile && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeChecklistFile(item.document_type_code)}
                        >
                          <X size={14} />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="text-xs text-[#64748B] mt-3">
              Required checklist documents must be uploaded before submission. Textract OCR will run after upload.
            </p>
          </CardContent>
        </Card>
      )}

      <Card className={`rounded-sm border ${isFormLocked ? 'opacity-50 pointer-events-none' : ''}`}>
        <CardHeader>
          <CardTitle className="text-base font-semibold">Step 2: Request Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {isFormLocked && (
            <div className="text-sm text-[#854D0E] bg-[#FEF9C3] border border-[#EAB308]/30 rounded-sm p-3">
              Select a request type first before filling out the request form.
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.05em] font-medium text-[#475569]">Vendor / Payee</Label>
              <Select value={form.vendor_id} onValueChange={v => handleChange('vendor_id', v)}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="vendor-select">
                  <SelectValue placeholder="Select vendor / payee" />
                </SelectTrigger>
                <SelectContent className="rounded-sm max-h-60">
                  {vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.05em] font-medium text-[#475569]">Contract</Label>
              {form.contract_id ? (
                <div className="flex items-center gap-2 mt-1">
                  <Select value={form.contract_id} onValueChange={v => handleChange('contract_id', v)}>
                    <SelectTrigger className="rounded-sm flex-1" data-testid="contract-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-sm max-h-60">
                      {contracts.map(c => <SelectItem key={c.id} value={c.id}>{c.contract_code} - {c.title}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button variant="ghost" size="sm" className="rounded-sm h-9 px-2 text-[#94A3B8] hover:text-[#EF4444]" onClick={() => handleChange('contract_id', '')} data-testid="clear-contract-btn">
                    <X size={14} />
                  </Button>
                </div>
              ) : (
                <Select value={undefined} onValueChange={v => handleChange('contract_id', v)}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="contract-select">
                    <SelectValue placeholder="Select contract (optional)" />
                  </SelectTrigger>
                  <SelectContent className="rounded-sm max-h-60">
                    {contracts.length === 0 ? (
                      <div className="px-2 py-3 text-sm text-[#94A3B8] text-center">
                        {form.vendor_id ? 'No contracts for this vendor' : 'Select a vendor first'}
                      </div>
                    ) : (
                      contracts.map(c => <SelectItem key={c.id} value={c.id}>{c.contract_code} - {c.title}</SelectItem>)
                    )}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.05em] font-medium text-[#475569]">Category</Label>
              {/* badyet-cost-centre */}
              <div className="mb-4">
                <Label>Department <span className="text-red-500">*</span></Label>
                <Select value={form.cost_centre} onValueChange={v => handleChange('cost_centre', v)}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="cost-centre-select">
                    <SelectValue placeholder={ccUnavailable ? 'Departments unavailable' : 'Select department'} />
                  </SelectTrigger>
                  <SelectContent>
                    {costCentres.map(cc => (
                      <SelectItem key={cc.code} value={cc.code}>{cc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {ccUnavailable && (
                  <p className="text-xs text-amber-700 mt-1">
                    {ccUnavailable} &mdash; this request cannot be checked against a budget until a department is set.
                  </p>
                )}
              </div>
              <Select value={form.category} onValueChange={v => handleChange('category', v)}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="rounded-sm">
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.05em] font-medium text-[#475569]">Payment Type</Label>
              <Select value={form.payment_type} onValueChange={v => handleChange('payment_type', v)}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="payment-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-sm">
                  {PAYMENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.05em] font-medium text-[#475569]">Amount (PHP)</Label>
              <Input
                type="number"
                step="0.01"
                value={form.amount}
                onChange={e => handleChange('amount', e.target.value)}
                placeholder="0.00"
                className="rounded-sm mt-1"
                data-testid="amount-input"
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.05em] font-medium text-[#475569]">Needed / Due Date</Label>
              <Input
                type="date"
                value={form.due_date}
                onChange={e => handleChange('due_date', e.target.value)}
                className="rounded-sm mt-1"
                data-testid="due-date-input"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.05em] font-medium text-[#475569]">Description / Purpose</Label>
            <Textarea
              value={form.description}
              onChange={e => handleChange('description', e.target.value)}
              placeholder="Describe the purpose of this request"
              className="rounded-sm mt-1"
              rows={3}
              data-testid="description-input"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.05em] font-medium text-[#475569]">Notes</Label>
            <Textarea
              value={form.notes}
              onChange={e => handleChange('notes', e.target.value)}
              placeholder="Additional notes"
              className="rounded-sm mt-1"
              rows={2}
              data-testid="notes-input"
            />
          </div>
        </CardContent>
      </Card>

      <Card className={`rounded-sm border ${isFormLocked ? 'opacity-50 pointer-events-none' : ''}`}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <PaperclipHorizontal size={18} /> Optional Supporting Documents
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div
            className="border-2 border-dashed border-[#E2E8F0] rounded-sm p-6 text-center cursor-pointer hover:border-[#0055FF]/40 hover:bg-[#0055FF]/5 transition-colors"
            onClick={() => fileInputRef.current?.click()}
            data-testid="file-drop-zone"
          >
            <PaperclipHorizontal size={24} className="mx-auto text-[#94A3B8] mb-2" />
            <p className="text-sm text-[#475569]">Click to attach supporting documents</p>
            <p className="text-xs text-[#94A3B8] mt-1">PDF, DOC, DOCX, XLS, XLSX, JPG, PNG (max 25MB each)</p>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              multiple
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
              onChange={handleFileAdd}
              data-testid="file-input"
            />
          </div>

          {files.length > 0 && (
            <div className="space-y-2">
              {files.map((f, i) => (
                <div key={i} className="flex items-center justify-between py-2 px-3 bg-[#F1F5F9] rounded-sm">
                  <div className="flex items-center gap-2">
                    <File size={16} className="text-[#475569]" />
                    <span className="text-sm text-[#0F172A]">{f.name}</span>
                    <span className="text-xs text-[#94A3B8]">({(f.size / 1024).toFixed(0)} KB)</span>
                  </div>
                  <button onClick={() => removeFile(i)} className="text-[#94A3B8] hover:text-[#EF4444] transition-colors" data-testid={`remove-file-${i}`}>
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {files.length > 0 && (
            <div className="flex items-center gap-2 text-sm text-[#166534] bg-[#DCFCE7] border border-[#BBF7D0] rounded-sm p-3">
              <CheckCircle size={18} />
              {files.length} file(s) attached. Textract OCR will run after upload.
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex gap-3 justify-end">
        <Button variant="outline" className="rounded-sm" onClick={() => navigate('/payments')} data-testid="cancel-btn">
          Cancel
        </Button>

        <Button variant="outline" className="rounded-sm" onClick={() => handleSubmit(true)} disabled={loading || isFormLocked} data-testid="save-draft-btn">
          {isEdit ? 'Save Changes' : 'Save as Draft'}
        </Button>

        <Button className="rounded-sm bg-[#0055FF] hover:bg-[#0043CC]" onClick={() => handleSubmit(false)} disabled={loading || isFormLocked} data-testid="submit-btn">
          {loading ? 'Processing...' : isEdit ? 'Resubmit' : 'Submit Request'}
        </Button>
      </div>
    </div>
  );
}
